/**
 * QuestSystem -- Manages quest state, progression, completion, and rewards.
 *
 * Loads quest definitions from quests.json. Tracks active, completed, and
 * abandoned quests. Listens to EventBus for auto-progression triggers
 * (combat victories, region changes, item pickups).
 *
 * Supports procedural side quest generation per region.
 *
 * @module QuestSystem
 */

import eventBus from '../core/EventBus.js';
import debugLog from '../debug/DebugLog.js';
import questsData from '../data/quests.json';

/**
 * Procedural side quest templates for generation.
 */
const SIDE_QUEST_TEMPLATES = [
  {
    titleTemplate: 'Hunt the {enemy}',
    descTemplate: 'Dangerous {enemy} creatures have been spotted near {region}. Thin their numbers before they become a real threat.',
    subtype: 'kill',
    stepsFactory: (regionId, enemyId, count) => [
      { text: `Defeat ${count} ${enemyId} in the region`, type: 'kill', targetEnemyId: enemyId, targetCount: count },
    ],
  },
  {
    titleTemplate: 'Scout {region}',
    descTemplate: 'Explore the area around {region} and report back on what you find.',
    subtype: 'discover',
    stepsFactory: (regionId) => [
      { text: `Explore ${regionId}`, type: 'discover', targetRegionId: regionId },
    ],
  },
  {
    titleTemplate: 'Road Patrol',
    descTemplate: 'The roads near {region} have become dangerous. Patrol the area and deal with any threats.',
    subtype: 'kill',
    stepsFactory: (regionId, enemyId, count) => [
      { text: `Travel through ${regionId}`, type: 'discover', targetRegionId: regionId },
      { text: `Defeat ${count} hostiles`, type: 'kill', targetEnemyId: enemyId, targetCount: count },
    ],
  },
];

/**
 * NPC name lists for quest NPC generation.
 */
const NPC_FIRST_NAMES = [
  'Aldric', 'Brenna', 'Cedric', 'Dahlia', 'Edric', 'Faye', 'Gareth',
  'Helena', 'Ivor', 'Jasmine', 'Kellan', 'Lyra', 'Magnus', 'Nara',
  'Oswald', 'Petra', 'Quinn', 'Rosalind', 'Silas', 'Thea',
];
const NPC_TITLES = [
  'the Tanner', 'the Smith', 'the Herbalist', 'the Scout', 'the Elder',
  'the Merchant', 'the Guard Captain', 'the Innkeeper', 'the Farmer', 'the Hunter',
];

/** Counter for generated quest IDs. */
let _nextGenId = 1;

class QuestSystem {
  constructor() {
    /** @type {Map<string, object>} All quest definitions keyed by id */
    this._definitions = new Map();

    /** @type {Map<string, object>} Active quests: id -> { quest, currentStep, killProgress, startedAt } */
    this._active = new Map();

    /** @type {Map<string, object>} Completed quests */
    this._completed = new Map();

    /** @type {Set<string>} Abandoned quest IDs */
    this._abandoned = new Set();

    /** @type {object|null} Currently tracked quest (shown in HUD tracker) */
    this._trackedQuestId = null;

    /** @type {object|null} Party reference */
    this._party = null;

    /** Event handler references for cleanup */
    this._eventHandlers = {};

    // Load quest data
    this._loadQuests();
  }

  /**
   * Load all quest definitions from the imported JSON data.
   */
  _loadQuests() {
    if (!Array.isArray(questsData)) {
      debugLog.warn('QuestSystem: quests.json is not an array');
      return;
    }

    for (const quest of questsData) {
      if (quest.id) {
        this._definitions.set(quest.id, structuredClone(quest));
      }
    }

    debugLog.info(`QuestSystem: Loaded ${this._definitions.size} quest definitions`);
  }

  /**
   * Initialize the quest system. Subscribe to game events.
   * @param {object} [party] Party reference
   */
  init(party = null) {
    this._party = party;
    this._subscribeEvents();
    debugLog.info('QuestSystem: Initialized');
  }

  /**
   * Set the party reference.
   * @param {object} party
   */
  setParty(party) {
    this._party = party;
  }

  // ── Quest Management ──────────────────────────────────

  /**
   * Start a quest by ID.
   * @param {string} questId
   * @returns {boolean} true if quest started successfully
   */
  startQuest(questId) {
    if (this._active.has(questId)) {
      debugLog.warn(`QuestSystem: Quest "${questId}" already active`);
      return false;
    }

    if (this._completed.has(questId)) {
      debugLog.warn(`QuestSystem: Quest "${questId}" already completed`);
      return false;
    }

    const def = this._definitions.get(questId);
    if (!def) {
      debugLog.warn(`QuestSystem: Quest definition not found: ${questId}`);
      return false;
    }

    const questState = {
      quest: structuredClone(def),
      currentStep: 0,
      killProgress: {},   // { enemyId: count }
      fetchProgress: {},  // { itemId: count }
      startedAt: Date.now(),
    };

    // Initialize kill tracking for current step
    this._initStepTracking(questState);

    this._active.set(questId, questState);

    // Auto-track if no quest is tracked, or if this is a main quest
    if (!this._trackedQuestId || def.type === 'main') {
      this._trackedQuestId = questId;
    }

    debugLog.event(`QuestSystem: Started quest "${def.title}" (${questId})`);
    eventBus.emit('quest:start', { questId, quest: def });

    return true;
  }

  /**
   * Update quest progress to a specific step.
   * @param {string} questId
   * @param {number} stepIndex
   */
  updateQuest(questId, stepIndex) {
    const state = this._active.get(questId);
    if (!state) return;

    if (stepIndex <= state.currentStep) return;
    if (stepIndex >= state.quest.steps.length) {
      this.completeQuest(questId);
      return;
    }

    state.currentStep = stepIndex;
    this._initStepTracking(state);

    debugLog.event(`QuestSystem: Quest "${state.quest.title}" progressed to step ${stepIndex}`);
    eventBus.emit('quest:progress', {
      questId,
      stepIndex,
      step: state.quest.steps[stepIndex],
      quest: state.quest,
    });
  }

  /**
   * Advance the quest to the next step.
   * @param {string} questId
   */
  advanceQuest(questId) {
    const state = this._active.get(questId);
    if (!state) return;

    const nextStep = state.currentStep + 1;
    if (nextStep >= state.quest.steps.length) {
      this.completeQuest(questId);
    } else {
      this.updateQuest(questId, nextStep);
    }
  }

  /**
   * Complete a quest and grant rewards.
   * @param {string} questId
   * @returns {boolean} true if quest was completed
   */
  completeQuest(questId) {
    const state = this._active.get(questId);
    if (!state) {
      debugLog.warn(`QuestSystem: Cannot complete quest "${questId}" — not active`);
      return false;
    }

    // Move to completed
    this._active.delete(questId);
    this._completed.set(questId, {
      quest: state.quest,
      completedAt: Date.now(),
    });

    // Grant rewards
    const rewards = state.quest.rewards;
    if (rewards) {
      this._grantRewards(rewards);
    }

    // Update tracker
    if (this._trackedQuestId === questId) {
      this._trackedQuestId = this._getNextTrackableQuest();
    }

    debugLog.event(`QuestSystem: Completed quest "${state.quest.title}" (${questId})`);
    eventBus.emit('quest:complete', {
      questId,
      quest: state.quest,
      rewards,
    });

    return true;
  }

  /**
   * Abandon a quest (only side quests can be abandoned).
   * @param {string} questId
   * @returns {boolean} true if quest was abandoned
   */
  abandonQuest(questId) {
    const state = this._active.get(questId);
    if (!state) {
      debugLog.warn(`QuestSystem: Cannot abandon quest "${questId}" — not active`);
      return false;
    }

    if (state.quest.type === 'main') {
      debugLog.warn(`QuestSystem: Cannot abandon main quest "${questId}"`);
      return false;
    }

    this._active.delete(questId);
    this._abandoned.add(questId);

    // Update tracker
    if (this._trackedQuestId === questId) {
      this._trackedQuestId = this._getNextTrackableQuest();
    }

    debugLog.event(`QuestSystem: Abandoned quest "${state.quest.title}" (${questId})`);
    eventBus.emit('quest:abandon', { questId, quest: state.quest });

    return true;
  }

  // ── Queries ──────────────────────────────────────────

  /**
   * Get all active quests.
   * @returns {object[]} Array of { questId, quest, currentStep, killProgress }
   */
  getActiveQuests() {
    const result = [];
    for (const [questId, state] of this._active) {
      result.push({
        questId,
        quest: state.quest,
        currentStep: state.currentStep,
        killProgress: { ...state.killProgress },
        fetchProgress: { ...state.fetchProgress },
      });
    }
    return result;
  }

  /**
   * Get all completed quests.
   * @returns {object[]}
   */
  getCompletedQuests() {
    const result = [];
    for (const [questId, state] of this._completed) {
      result.push({
        questId,
        quest: state.quest,
        completedAt: state.completedAt,
      });
    }
    return result;
  }

  /**
   * Get a specific active quest state.
   * @param {string} questId
   * @returns {object|null}
   */
  getQuestState(questId) {
    return this._active.get(questId) || null;
  }

  /**
   * Get the currently tracked quest ID.
   * @returns {string|null}
   */
  getTrackedQuestId() {
    return this._trackedQuestId;
  }

  /**
   * Set the tracked quest.
   * @param {string|null} questId
   */
  setTrackedQuest(questId) {
    this._trackedQuestId = questId;
    eventBus.emit('quest:tracked-changed', { questId });
  }

  /**
   * Get a quest definition by ID.
   * @param {string} questId
   * @returns {object|null}
   */
  getDefinition(questId) {
    return this._definitions.get(questId) || null;
  }

  /**
   * Check if a quest is active.
   * @param {string} questId
   * @returns {boolean}
   */
  isActive(questId) {
    return this._active.has(questId);
  }

  /**
   * Check if a quest is completed.
   * @param {string} questId
   * @returns {boolean}
   */
  isCompleted(questId) {
    return this._completed.has(questId);
  }

  // ── Procedural Side Quest Generation ──────────────────

  /**
   * Generate a random side quest for a region.
   * @param {string} regionId
   * @param {number} [level=1] Party level for scaling rewards
   * @returns {object} Generated quest definition
   */
  generateSideQuest(regionId, level = 1) {
    const template = SIDE_QUEST_TEMPLATES[Math.floor(Math.random() * SIDE_QUEST_TEMPLATES.length)];

    // Pick a random enemy for the region
    const enemies = this._getRegionEnemies(regionId);
    const enemyId = enemies.length > 0
      ? enemies[Math.floor(Math.random() * enemies.length)]
      : 'bandit';

    const killCount = 2 + Math.floor(Math.random() * 4); // 2-5
    const steps = template.stepsFactory(regionId, enemyId, killCount);

    // Generate NPC
    const npc = this._generateNPC();

    // Scale rewards with level
    const goldReward = 15 + level * 10 + Math.floor(Math.random() * 20);
    const xpReward = 50 + level * 30;

    const questId = `gen_${regionId}_${_nextGenId++}`;
    const regionName = this._getRegionName(regionId);

    const quest = {
      id: questId,
      title: template.titleTemplate
        .replace('{enemy}', this._capitalize(enemyId))
        .replace('{region}', regionName),
      description: template.descTemplate
        .replace('{enemy}', enemyId)
        .replace('{region}', regionName),
      type: 'side',
      subtype: template.subtype,
      regionId,
      npc,
      generated: true,
      steps,
      rewards: {
        gold: goldReward,
        xp: xpReward,
      },
    };

    // Register the generated quest
    this._definitions.set(questId, structuredClone(quest));

    debugLog.info(`QuestSystem: Generated side quest "${quest.title}" (${questId})`);
    return quest;
  }

  /**
   * Generate a random quest NPC.
   * @returns {{ name: string, title: string }}
   */
  _generateNPC() {
    const firstName = NPC_FIRST_NAMES[Math.floor(Math.random() * NPC_FIRST_NAMES.length)];
    const title = NPC_TITLES[Math.floor(Math.random() * NPC_TITLES.length)];
    return { name: `${firstName} ${title}` };
  }

  // ── Event Trigger Checking ────────────────────────────

  /**
   * Check all active quests for trigger conditions.
   * Called when game events occur (combat victory, region change, etc.)
   * @param {string} eventType - 'kill', 'discover', 'fetch'
   * @param {object} data - Event-specific data
   */
  checkQuestTriggers(eventType, data) {
    for (const [questId, state] of this._active) {
      const step = state.quest.steps[state.currentStep];
      if (!step) continue;

      let stepComplete = false;

      switch (step.type) {
        case 'kill':
          if (eventType === 'kill' && data.enemyId === step.targetEnemyId) {
            state.killProgress[data.enemyId] = (state.killProgress[data.enemyId] || 0) + (data.count || 1);
            if (state.killProgress[data.enemyId] >= step.targetCount) {
              stepComplete = true;
            }
            // Emit progress even if not complete
            eventBus.emit('quest:progress', {
              questId,
              stepIndex: state.currentStep,
              step,
              quest: state.quest,
              killProgress: state.killProgress,
            });
          }
          break;

        case 'discover':
          if (eventType === 'discover' && data.regionId === step.targetRegionId) {
            stepComplete = true;
          }
          break;

        case 'fetch':
          if (eventType === 'fetch' && data.itemId === step.targetItemId) {
            state.fetchProgress[data.itemId] = (state.fetchProgress[data.itemId] || 0) + (data.count || 1);
            if (state.fetchProgress[data.itemId] >= step.targetCount) {
              stepComplete = true;
            }
          }
          break;
      }

      if (stepComplete) {
        this.advanceQuest(questId);
      }
    }
  }

  // ── Event Subscriptions ──────────────────────────────

  _subscribeEvents() {
    // Combat victory — check kill objectives
    this._eventHandlers.onCombatVictory = (data) => {
      if (!data || !data.rewards) return;
      const enemies = data.rewards.enemies || data.rewards.defeatedEnemies || [];
      for (const enemy of enemies) {
        if (enemy && enemy.id) {
          this.checkQuestTriggers('kill', { enemyId: enemy.id, count: 1 });
        }
      }
    };
    eventBus.on('combat:victory', this._eventHandlers.onCombatVictory);

    // Region change — check discover objectives
    this._eventHandlers.onRegionChange = (data) => {
      if (data && data.to) {
        this.checkQuestTriggers('discover', { regionId: data.to });
      }
    };
    eventBus.on('travel:region-change', this._eventHandlers.onRegionChange);

    // Item pickup — check fetch objectives
    this._eventHandlers.onItemAdd = (data) => {
      if (data && data.item && data.item.templateId) {
        this.checkQuestTriggers('fetch', { itemId: data.item.templateId, count: 1 });
      }
    };
    eventBus.on('inventory:add', this._eventHandlers.onItemAdd);

    // Dialog quest trigger
    this._eventHandlers.onDialogReward = (data) => {
      if (data && data.type === 'quest' && data.questId) {
        this.startQuest(data.questId);
      }
    };
    eventBus.on('dialog:reward', this._eventHandlers.onDialogReward);
  }

  _unsubscribeEvents() {
    if (this._eventHandlers.onCombatVictory) {
      eventBus.off('combat:victory', this._eventHandlers.onCombatVictory);
    }
    if (this._eventHandlers.onRegionChange) {
      eventBus.off('travel:region-change', this._eventHandlers.onRegionChange);
    }
    if (this._eventHandlers.onItemAdd) {
      eventBus.off('inventory:add', this._eventHandlers.onItemAdd);
    }
    if (this._eventHandlers.onDialogReward) {
      eventBus.off('dialog:reward', this._eventHandlers.onDialogReward);
    }
    this._eventHandlers = {};
  }

  // ── Private Helpers ──────────────────────────────────

  /**
   * Initialize tracking for the current step of a quest.
   */
  _initStepTracking(state) {
    const step = state.quest.steps[state.currentStep];
    if (!step) return;

    if (step.type === 'kill' && step.targetEnemyId) {
      // Reset kill progress for this enemy if not already tracked
      if (!(step.targetEnemyId in state.killProgress)) {
        state.killProgress[step.targetEnemyId] = 0;
      }
    }
    if (step.type === 'fetch' && step.targetItemId) {
      if (!(step.targetItemId in state.fetchProgress)) {
        state.fetchProgress[step.targetItemId] = 0;
      }
    }
  }

  /**
   * Grant quest rewards to the party.
   */
  _grantRewards(rewards) {
    if (!this._party) return;

    if (rewards.gold) {
      this._party.gold = (this._party.gold || 0) + rewards.gold;
      debugLog.info(`QuestSystem: Granted ${rewards.gold} gold`);
    }

    if (rewards.xp) {
      // XP distributed to all party members
      for (const member of this._party.members) {
        member.xp = (member.xp || 0) + rewards.xp;
      }
      debugLog.info(`QuestSystem: Granted ${rewards.xp} XP`);
    }

    if (rewards.items && Array.isArray(rewards.items)) {
      // Emit item rewards for inventory system to handle
      eventBus.emit('quest:reward-items', { items: rewards.items });
      debugLog.info(`QuestSystem: Granted items: ${rewards.items.join(', ')}`);
    }
  }

  /**
   * Get the next trackable quest (prefer main quests).
   * @returns {string|null}
   */
  _getNextTrackableQuest() {
    // Prefer main quests
    for (const [questId, state] of this._active) {
      if (state.quest.type === 'main') return questId;
    }
    // Fall back to any active quest
    for (const [questId] of this._active) {
      return questId;
    }
    return null;
  }

  /**
   * Get enemies for a region from DataStore.
   * @param {string} regionId
   * @returns {string[]}
   */
  _getRegionEnemies(regionId) {
    try {
      // Dynamic import would be circular, so we check window
      const dataStore = window.__debug?.dataStore;
      if (dataStore) {
        const region = dataStore.getById('regions', regionId);
        if (region && region.enemies) return region.enemies;
      }
    } catch { /* ignore */ }
    return ['bandit', 'wolf', 'goblin', 'spider'];
  }

  /**
   * Get a region's display name.
   * @param {string} regionId
   * @returns {string}
   */
  _getRegionName(regionId) {
    try {
      const dataStore = window.__debug?.dataStore;
      if (dataStore) {
        const region = dataStore.getById('regions', regionId);
        if (region) return region.name;
      }
    } catch { /* ignore */ }
    return regionId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /**
   * Capitalize first letter.
   * @param {string} str
   * @returns {string}
   */
  _capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // ── Serialization ────────────────────────────────────

  /**
   * Serialize quest state for saving.
   * @returns {object}
   */
  toJSON() {
    const active = {};
    for (const [id, state] of this._active) {
      active[id] = {
        currentStep: state.currentStep,
        killProgress: { ...state.killProgress },
        fetchProgress: { ...state.fetchProgress },
        startedAt: state.startedAt,
      };
    }

    const completed = {};
    for (const [id, state] of this._completed) {
      completed[id] = { completedAt: state.completedAt };
    }

    return {
      active,
      completed,
      abandoned: [...this._abandoned],
      trackedQuestId: this._trackedQuestId,
    };
  }

  /**
   * Restore quest state from save data.
   * @param {object} data
   */
  fromJSON(data) {
    if (!data) return;

    // Restore active quests
    if (data.active) {
      for (const [questId, savedState] of Object.entries(data.active)) {
        const def = this._definitions.get(questId);
        if (!def) continue;

        this._active.set(questId, {
          quest: structuredClone(def),
          currentStep: savedState.currentStep || 0,
          killProgress: savedState.killProgress || {},
          fetchProgress: savedState.fetchProgress || {},
          startedAt: savedState.startedAt || Date.now(),
        });
      }
    }

    // Restore completed quests
    if (data.completed) {
      for (const [questId, savedState] of Object.entries(data.completed)) {
        const def = this._definitions.get(questId);
        if (!def) continue;

        this._completed.set(questId, {
          quest: structuredClone(def),
          completedAt: savedState.completedAt || Date.now(),
        });
      }
    }

    // Restore abandoned
    if (data.abandoned) {
      for (const id of data.abandoned) {
        this._abandoned.add(id);
      }
    }

    // Restore tracked quest
    if (data.trackedQuestId && this._active.has(data.trackedQuestId)) {
      this._trackedQuestId = data.trackedQuestId;
    }

    debugLog.info(`QuestSystem: Restored ${this._active.size} active, ${this._completed.size} completed quests`);
  }

  /**
   * Clean up event subscriptions.
   */
  destroy() {
    this._unsubscribeEvents();
  }
}

// Singleton
const questSystem = new QuestSystem();
export default questSystem;
